import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '';

export type PoStatus = 'RESERVED' | 'ACTIVE' | 'INACTIVE';

export type PoType =
  | 'HIRED_EQUIPMENT'
  | 'SUBCONTRACTOR'
  | 'MATERIAL'
  | 'MECHANICAL_PARTS'
  | 'RENTALS'
  | 'OUTSIDE_WORK';

/** Display labels for PoType. The enum values are storage; these are what people read. */
export const PO_TYPE_LABELS: Record<PoType, string> = {
  HIRED_EQUIPMENT: 'Hired Equipment',
  SUBCONTRACTOR: 'Subcontractor',
  MATERIAL: 'Material',
  MECHANICAL_PARTS: 'Mechanical Parts',
  RENTALS: 'Rentals',
  OUTSIDE_WORK: 'Outside Work',
};

export const PO_TYPE_OPTIONS = Object.entries(PO_TYPE_LABELS).map(([value, label]) => ({
  value: value as PoType,
  label,
}));

export interface AreaRef {
  id: number;
  name: string;
  code?: string | null;
}

export interface PurchaseOrderVendorRef {
  id: number;
  name: string;
  status: string;
}

export interface PoConsumption {
  /** Value on efforts whose form has been approved — money that is payable. */
  approved: number;
  /** Value on efforts still awaiting approval. Real spend, just not payable yet. */
  submitted: number;
  /** amount - (approved + submitted). Negative when the PO is over its ceiling. */
  remaining: number;
  amount: number;
  /**
   * True when the figure cannot be computed at all, so the UI renders "not available" rather
   * than a confident zero. A zero meaning "nothing spent" and a zero meaning "we cannot tell"
   * are different facts and must not look the same.
   */
  pending: boolean;
}

export type PoConsumptionBucket = 'approved' | 'submitted' | 'excluded';

/** One effort booked against a PO number — the line-level view of the consumption bar. */
export interface PoConsumptionEffort {
  id: number;
  type?: string | null;
  description?: string | null;
  quantity?: number | null;
  rate?: number | null;
  unitOfMeasure?: string | null;
  date?: string | null;
  employeeName?: string | null;
  value: number;
  bucket: PoConsumptionBucket;
  formId: string;
  formName?: string | null;
  formStatus?: string | null;
  task?: { id: number; taskNumber?: string | null; name?: string | null } | null;
}

export interface PurchaseOrderLineItem {
  id: number;
  resourceId?: number | null;
  description?: string | null;
  quantity: number;
  unit?: string | null;
  /** Task sits on the LINE, not the PO — one PO routinely spans several tasks. */
  taskId?: number | null;
  task?: { id: number; taskNumber?: string | null; name: string } | null;
  /** The line's dollar value as the requisition submitted it. Null on imported lines. */
  amount?: number | null;
  /**
   * @deprecated The answer is per purchase order now (`PurchaseOrder.workInstructionsReviewed`).
   * This holds only the per-row answer older form versions submitted; do not display it.
   */
  workInstructionsReviewed?: WorkInstructionsReviewed;
  resource?: {
    id: number;
    name: string;
    resourceType: string;
    unit?: string | null;
    isFixedPrice: boolean;
  } | null;
}

/**
 * The three answers to "Work Instructions Reviewed". Mirrors
 * server/src/modules/purchase-order/work-instructions.ts, where a DB CHECK constraint pins the
 * same vocabulary - keep the two in step.
 */
export const WORK_INSTRUCTIONS_REVIEWED = {
  YES: 'Yes',
  LATER: "I'll do it later",
  NOT_APPLICABLE: 'Not Applicable',
} as const;

export type WorkInstructionsReviewed =
  (typeof WORK_INSTRUCTIONS_REVIEWED)[keyof typeof WORK_INSTRUCTIONS_REVIEWED];

export const WORK_INSTRUCTIONS_REVIEWED_VALUES: WorkInstructionsReviewed[] = [
  WORK_INSTRUCTIONS_REVIEWED.YES,
  WORK_INSTRUCTIONS_REVIEWED.LATER,
  WORK_INSTRUCTIONS_REVIEWED.NOT_APPLICABLE,
];

/** True when the reviewer deferred - the one answer the PO table warns about. */
export const isWorkInstructionsOutstanding = (
  value: string | null | undefined
): boolean => value === WORK_INSTRUCTIONS_REVIEWED.LATER;

export interface PurchaseOrder {
  id: number;
  poNumber: string;
  description?: string | null;
  vendorId?: number | null;
  vendor?: PurchaseOrderVendorRef | null;
  serviceAreaId: number;
  serviceArea?: AreaRef | null;
  foremanAreaId: number;
  foremanArea?: AreaRef | null;
  contractAreaId: number;
  contractArea?: AreaRef | null;
  type: PoType;
  amount: number;
  requestedByName?: string | null;
  requestedBy?: { id: number; name: string; lastName: string } | null;
  requestDate?: string | null;
  submissionId?: string | null;
  reference?: string | null;
  /** The purchaser's own reference, captured by the requisition form on creation. */
  purchaseReference?: string | null;
  /**
   * Whether the requester reviewed the work instructions with the subcontractor - one answer
   * per purchase order. `Yes` is terminal: the server refuses to move it back, and the UI hides
   * the buttons once it is set.
   */
  workInstructionsReviewed: WorkInstructionsReviewed;
  /** True when the answer is still "I'll do it later". Computed server-side. */
  workInstructionsOutstanding?: boolean;
  status: PoStatus;
  source: string;
  sequenceYear: number;
  createdAt: string;
  updatedAt: string;
  lineItems?: PurchaseOrderLineItem[];
  consumption?: PoConsumption;
  _count?: { lineItems: number };
}

export interface PurchaseOrderEvent {
  id: number;
  eventType: string;
  payload?: Record<string, unknown> | null;
  occurredAt: string;
  actor?: { id: number; name: string; lastName: string } | null;
}

export interface PurchaseOrderQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  /** Multi-select. Empty or omitted means unfiltered. */
  vendorIds?: number[];
  serviceAreaIds?: number[];
  foremanAreaIds?: number[];
  contractAreaIds?: number[];
  types?: PoType[];
  statuses?: PoStatus[];
  includeReserved?: boolean;
  /** Allowlisted server-side: poNumber, description, amount, type, status, requestDate, createdAt, requestedByName, vendor, serviceArea, foremanArea. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedPurchaseOrdersResponse {
  data: PurchaseOrder[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

class PurchaseOrderService {
  private baseUrl = `${BASE_URL}/purchase-orders`;

  private handleError(error: any): Error {
    if (error.response) {
      return new Error(
        error.response.data?.message || error.response.data?.error || 'An error occurred'
      );
    } else if (error.request) {
      return new Error('No response from server');
    }
    return new Error(error.message || 'An error occurred');
  }

  private toQuery(params: PurchaseOrderQueryParams): string {
    const q = new URLSearchParams();
    if (params.page) q.append('page', String(params.page));
    if (params.limit) q.append('limit', String(params.limit));
    if (params.search) q.append('search', params.search);
    // Comma-joined rather than repeated: shorter URLs, and the server accepts both.
    if (params.vendorIds?.length) q.append('vendorIds', params.vendorIds.join(','));
    if (params.serviceAreaIds?.length) q.append('serviceAreaIds', params.serviceAreaIds.join(','));
    if (params.foremanAreaIds?.length) q.append('foremanAreaIds', params.foremanAreaIds.join(','));
    if (params.contractAreaIds?.length) q.append('contractAreaIds', params.contractAreaIds.join(','));
    if (params.types?.length) q.append('types', params.types.join(','));
    if (params.statuses?.length) q.append('statuses', params.statuses.join(','));
    if (params.includeReserved) q.append('includeReserved', 'true');
    if (params.sortBy) q.append('sortBy', params.sortBy);
    if (params.sortOrder) q.append('sortOrder', params.sortOrder);
    return q.toString();
  }

  async getPurchaseOrdersPaginated(
    params: PurchaseOrderQueryParams = {}
  ): Promise<PaginatedPurchaseOrdersResponse> {
    try {
      const response = await axios.get(`${this.baseUrl}?${this.toQuery(params)}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAllPurchaseOrders(params: PurchaseOrderQueryParams = {}): Promise<PurchaseOrder[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/all?${this.toQuery(params)}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPurchaseOrderById(id: number): Promise<PurchaseOrder> {
    try {
      const response = await axios.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Line items for one PO, for the expandable row on the list.
   *
   * Deliberately reuses the detail endpoint rather than adding a `/line-items` route: the detail
   * response already carries them with their resource and task, and the audit trail (the only
   * genuinely heavy part) is a separate call. If the list ever expands many rows at once this
   * is the thing to revisit.
   */
  async getLineItems(id: number): Promise<PurchaseOrderLineItem[]> {
    const po = await this.getPurchaseOrderById(id);
    return po.lineItems ?? [];
  }

  async getEvents(id: number): Promise<PurchaseOrderEvent[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/${id}/events`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Efforts whose poNumber matches this PO — what the consumption bar is summing. */
  async getConsumptionEfforts(id: number): Promise<PoConsumptionEffort[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/${id}/efforts`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async setStatus(id: number, status: 'ACTIVE' | 'INACTIVE'): Promise<PurchaseOrder> {
    try {
      const response = await axios.patch(`${this.baseUrl}/${id}/status`, { status });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Record whether the requester reviewed the work instructions with the subcontractor.
   *
   * Requires po:work_instructions_manage; every change writes a PO history event server-side.
   * Moving a PO away from "Yes" is refused with 409 WORK_INSTRUCTIONS_LOCKED - the UI hides
   * the buttons in that case, and this is the backstop.
   */
  async setWorkInstructions(
    id: number,
    value: WorkInstructionsReviewed,
    note?: string
  ): Promise<Pick<PurchaseOrder, 'id' | 'workInstructionsReviewed'>> {
    try {
      const response = await axios.patch(`${this.baseUrl}/${id}/work-instructions`, {
        value,
        note,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async setLineItems(
    id: number,
    lineItems: Array<{
      resourceId?: number | null;
      description?: string | null;
      quantity: number;
      unit?: string | null;
    }>
  ): Promise<PurchaseOrderLineItem[]> {
    try {
      const response = await axios.put(`${this.baseUrl}/${id}/line-items`, { lineItems });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

export const purchaseOrderService = new PurchaseOrderService();
export default purchaseOrderService;
